import * as Yup from 'yup';

/**
 * useRoleNormalization Hook
 * 
 * Centralizes all role-related formatting, normalization, and configuration
 * Handles conversion between frontend (camelCase), backend (UPPER_CASE), and display formats
 * Provides field mappings, validation schemas, and data transformations
 */

const useRoleNormalization = () => {
  // Central role configuration
  /**
   * ⚠️ TWO COPIES OF THIS OBJECT EXIST — this one and the exported `roleConfig` lower down.
   * They are kept in sync BY HAND: the hook reads this one, the `*Util` exports read the other,
   * and a role added to only one makes the hook and the utils disagree about what a user is.
   */
  const roleConfig = {
    recipient: {
      normalized: 'recipient',
      cypherFormat: 'RECIPIENT',
      label: 'Reader',
      pluralLabel: 'Readers',
      icon: 'headphones',
      color: 'teal',
      profiles: ['recipient'],
      aliases: [
        'RECIPIENT',
        'recipient',
        'receiver',
        'RECEIVER',
        'Reader',
        'reader'
      ]
    },
    admin: {
      normalized: 'admin',           // Frontend internal format (camelCase)
      cypherFormat: 'ADMIN',         // Backend Neo4j/Cypher format
      label: 'Administrator',        // Display format
      pluralLabel: 'Administrators', // Plural display format
      icon: 'shield',               // Semantic UI icon
      color: 'red',                  // UI color
      profiles: ['admin', 'bookCreator', 'voiceActor'], // Allowed profiles
      aliases: [                     // All possible input formats
        'ADMIN',
        'admin',
        'Administrator',
        'administrator'
      ]
    },
    bookCreator: {
      normalized: 'bookCreator',
      cypherFormat: 'BOOK_CREATOR',
      label: 'Book Creator',
      pluralLabel: 'Book Creators',
      icon: 'book',
      color: 'green',
      profiles: ['bookCreator', 'voiceActor'],
      aliases: [
        'BOOK_CREATOR',
        'book_creator',
        'bookCreator',
        'Book Creator',
        'book creator'
      ]
    },
    voiceActor: {
      normalized: 'voiceActor',
      cypherFormat: 'VOICE_ACTOR',
      label: 'Voice Actor',
      pluralLabel: 'Voice Actors',
      icon: 'microphone',
      color: 'blue',
      profiles: ['voiceActor'],
      aliases: [
        'VOICE_ACTOR',
        'voice_actor',
        'voiceActor',
        'Voice Actor',
        'voice actor'
      ]
    }
  };

  /**
   * Normalizes any role format to standard camelCase format
   * @param {string} role - Role in any format
   * @returns {string} - Normalized role (admin, bookCreator, voiceActor)
   */
  const normalizeRole = (role) => {
    if (!role) return null;

    // Check each role config for matching alias
    for (const [key, config] of Object.entries(roleConfig)) {
      if (config.aliases.includes(role) || key === role) {
        return key;
      }
    }

    // If no match found, try lowercase comparison
    const lowercaseRole = role.toLowerCase();
    for (const [key, config] of Object.entries(roleConfig)) {
      if (config.aliases.some(alias => alias.toLowerCase() === lowercaseRole)) {
        return key;
      }
    }

    return role; // Return original if no match
  };

  /**
   * Converts normalized role to Cypher format for backend
   * @param {string} role - Role in any format
   * @returns {string} - Cypher format (ADMIN, BOOK_CREATOR, VOICE_ACTOR)
   */
  const toCypherFormat = (role) => {
    const normalized = normalizeRole(role);
    return roleConfig[normalized]?.cypherFormat || role;
  };

  /**
   * Converts Cypher format to normalized format
   * @param {string} cypherRole - Role in Cypher format
   * @returns {string} - Normalized role
   */
  const fromCypherFormat = (cypherRole) => {
    if (!cypherRole) return null;

    const entry = Object.values(roleConfig).find(
      config => config.cypherFormat === cypherRole
    );
    return entry?.normalized || normalizeRole(cypherRole);
  };

  /**
   * Gets display label for role
   * @param {string} role - Role in any format
   * @param {boolean} plural - Whether to return plural form
   * @returns {string} - Display label
   */
  const getRoleLabel = (role, plural = false) => {
    const normalized = normalizeRole(role);
    const config = roleConfig[normalized];
    if (!config) return role; // Fallback to original
    return plural ? config.pluralLabel : config.label;
  };

  /**
   * Gets icon name for role
   * @param {string} role - Role in any format
   * @returns {string} - Semantic UI icon name
   */
  const getRoleIcon = (role) => {
    const normalized = normalizeRole(role);
    return roleConfig[normalized]?.icon || 'user';
  };

  /**
   * Gets UI color for role
   * @param {string} role - Role in any format
   * @returns {string} - Color name for Semantic UI
   */
  const getRoleColor = (role) => {
    const normalized = normalizeRole(role);
    return roleConfig[normalized]?.color || 'grey';
  };

  /**
   * Gets allowed profiles for a role
   * @param {string} role - Role in any format
   * @returns {Array<string>} - Array of allowed profile types
   */
  const getRoleProfiles = (role) => {
    const normalized = normalizeRole(role);
    return roleConfig[normalized]?.profiles || [];
  };

  /**
   * Gets filter options for role dropdowns
   * @param {boolean} includeAll - Whether to include "All Roles" option
   * @returns {Array} - Options for Semantic UI Dropdown
   */
  const getRoleFilterOptions = (includeAll = true) => {
    const options = includeAll ?
      [{ key: 'all', value: 'all', text: 'All Roles' }] : [];

    return [
      ...options,
      { key: 'admin', value: 'admin', text: 'Administrators' },
      { key: 'voiceActor', value: 'voiceActor', text: 'Voice Actors' },
      { key: 'bookCreator', value: 'bookCreator', text: 'Book Creators' }
    ];
  };

  /**
   * Gets role selection options with icons
   * @returns {Array} - Options for role selection dropdowns
   */
  const getRoleSelectionOptions = () => {
    return Object.entries(roleConfig).map(([key, config]) => ({
      key: key,
      value: key,
      text: config.label,
      icon: config.icon,
      color: config.color
    }));
  };

  /**
   * Gets user type options for dropdowns (simplified format)
   * @returns {Array} - Options for user type dropdowns
   */
  const getUserTypeOptions = () => {
    return [
      { key: 'voiceActor', value: 'voiceActor', text: getRoleLabel('voiceActor') },
      { key: 'bookCreator', value: 'bookCreator', text: getRoleLabel('bookCreator') },
      { key: 'admin', value: 'admin', text: getRoleLabel('admin') }
    ];
  };

  /**
   * Checks if a profile type is allowed for a role
   * @param {string} role - Role in any format
   * @param {string} profileType - Profile type to check
   * @returns {boolean} - Whether the profile is allowed
   */
  const canHaveProfile = (role, profileType) => {
    const allowedProfiles = getRoleProfiles(role);
    return allowedProfiles.includes(profileType);
  };

  /**
   * Validates if a role is valid
   * @param {string} role - Role to validate
   * @returns {boolean} - Whether the role is valid
   */
  const isValidRole = (role) => {
    const normalized = normalizeRole(role);
    return normalized in roleConfig;
  };

  /**
   * Gets role hierarchy level (for permissions)
   * @param {string} role - Role in any format
   * @returns {number} - Hierarchy level (higher = more permissions)
   */
  const getRoleHierarchy = (role) => {
    const hierarchy = {
      admin: 3,
      bookCreator: 2,
      voiceActor: 1
    };
    const normalized = normalizeRole(role);
    return hierarchy[normalized] || 0;
  };

  /**
   * Compares two roles by hierarchy
   * @param {string} role1 - First role
   * @param {string} role2 - Second role
   * @returns {number} - Comparison result (-1, 0, 1)
   */
  const compareRoles = (role1, role2) => {
    const h1 = getRoleHierarchy(role1);
    const h2 = getRoleHierarchy(role2);
    return h1 - h2;
  };

  /**
   * Formats role for display (alias for getRoleLabel)
   * @param {string} role - Role in any format
   * @returns {string} - Formatted role label
   */
  const formatRole = (role) => getRoleLabel(role);

  /**
   * Extracts role from profile type string (e.g., "VoiceActorProfile" -> "voiceActor")
   * @param {string} profileType - Profile type from backend
   * @returns {string|null} - Normalized role or null if invalid
   */
  const extractRoleFromProfileType = (profileType) => {
    if (!profileType) return null;

    const profileTypeMap = {
      'AdminProfile': 'admin',
      'BookCreatorProfile': 'bookCreator',
      'VoiceActorProfile': 'voiceActor'
    };

    return profileTypeMap[profileType] || null;
  };

  /**
   * Validates profile array matches expected profiles for role
   * @param {string} role - User's primary role
   * @param {Array} profiles - Current profiles array
   * @returns {boolean} - Whether profiles are valid for role
   */
  const validateProfilesForRole = (role, profiles) => {
    if (!profiles || !Array.isArray(profiles)) return false;

    const expectedProfiles = getRoleProfiles(role);
    const actualProfileTypes = profiles.map(p =>
      p.type || extractRoleFromProfileType(p.profileType)
    );

    // Check if all expected profiles exist and no extra profiles
    return expectedProfiles.every(expected =>
      actualProfileTypes.includes(expected)
    ) && actualProfileTypes.every(actual =>
      expectedProfiles.includes(actual)
    );
  };

  /**
   * Filters profiles to only those allowed for role
   * @param {string} role - User's primary role
   * @param {Array} profiles - All profiles
   * @returns {Array} - Filtered profiles
   */
  const filterProfilesForRole = (role, profiles) => {
    if (!profiles || !Array.isArray(profiles)) return [];

    const allowedProfiles = getRoleProfiles(role);
    return profiles.filter(profile => {
      const profileType = profile.type || extractRoleFromProfileType(profile.profileType);
      return allowedProfiles.includes(profileType);
    });
  };

  /**
   * Determine primary role from a set of valid roles
   * Uses hierarchy: admin > bookCreator > voiceActor
   * @param {Object} validRoles - Object with boolean flags for each role
   * @returns {string|null} - Primary role based on hierarchy
   */
  const determinePrimaryRole = (validRoles) => {
    if (validRoles.admin) return 'admin';
    if (validRoles.bookCreator) return 'bookCreator';
    if (validRoles.voiceActor) return 'voiceActor';
    return null;
  };

  /**
   * Get default values for a specific profile type
   * @param {string} profileType - Profile type (admin, bookCreator, voiceActor)
   * @returns {Object} - Default values for the profile type
   */
  const getProfileDefaults = (profileType) => {
    const defaults = {
      admin: {
        adminLevel: 'standard',
        permissions: [],
        department: 'general',
        canManageUsers: true,
        canManageContent: true
      },
      bookCreator: {
        completedProjects: 0,
        specialties: [],
        genres: [],
        publishedBooks: 0,
        preferredNarrators: [],
        baseRate: 0,
        rating: 0,
        reviewCount: 0
      },
      voiceActor: {
        experienceLevel: 'BEGINNER',
        baseRate: 300,
        rating: 0,
        reviewCount: 0,
        specialties: [],
        skills: [],
        languages: ['English'],
        availability: 'OPEN',
        actorType: 'Authorator-ai',
        completedProjects: 0,
        hobbies: []
      }
    };

    const normalized = normalizeRole(profileType);
    return defaults[normalized] || {};
  };

  /**
   * Get empty profile template with all fields
   * @param {string} profileType - Profile type
   * @returns {Object} - Complete empty profile template
   */
  const getEmptyProfileTemplate = (profileType) => {
    const common = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      phones: [],
      street: '',
      city: '',
      state: '',
      zipcode: '',
      country: 'USA',
      description: '',
      profilePictureUrl: ''
    };

    return {
      ...common,
      ...getProfileDefaults(profileType)
    };
  };

  /**
   * Extract basic user data that should copy to all profiles
   * @param {Object} sourceData - Source data object
   * @returns {Object} - Extracted basic user data
   */
  const extractBasicUserData = (sourceData) => {
    if (!sourceData) return {};

    return {
      firstName: sourceData.firstName || '',
      lastName: sourceData.lastName || '',
      email: sourceData.email || '',
      phone: sourceData.phone || '',
      phones: sourceData.phones || (sourceData.phone ? [sourceData.phone] : []),
      street: sourceData.street || '',
      city: sourceData.city || '',
      state: sourceData.state || '',
      zipcode: sourceData.zipcode || sourceData.zipCode || '',
      country: sourceData.country || 'USA'
    };
  };

  /**
   * Merge profile data sources with proper priority
   * @param {Array} sources - Array of data sources in priority order
   * @returns {Object} - Merged data
   */
  const mergeProfileData = (sources) => {
    // Priority: first source overwrites later sources
    return sources.reverse().reduce((merged, source) => {
      if (!source) return merged;
      // Deep merge to preserve nested objects
      Object.keys(source).forEach(key => {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
          merged[key] = source[key];
        }
      });
      return merged;
    }, {});
  };

  /**
   * Analyze user profiles to determine primary role and completeness
   * @param {Array} userProfiles - Array of user profile objects
   * @returns {Object} - Analysis result with primaryRole, validRoles, isComplete, etc.
   */
  const analyzeUserProfiles = (userProfiles) => {
    if (!userProfiles || !Array.isArray(userProfiles) || userProfiles.length === 0) {
      return {
        primaryRole: null,
        validRoles: { admin: false, bookCreator: false, voiceActor: false, recipient: false },
        isComplete: false,
        hasVoiceActorProfile: false,
        hasBookCreatorProfile: false,
        hasAdminProfile: false,
        hasRecipientProfile: false,
        validationIssues: ['No profiles provided']
      };
    }

    const profileTypes = userProfiles.map(p => p.profileType);
    const userTypes = [...new Set(userProfiles.map(p => p.type))];

    // Check which profiles exist
    const hasVoiceActorProfile = profileTypes.includes('VoiceActorProfile');
    const hasBookCreatorProfile = profileTypes.includes('BookCreatorProfile');
    const hasAdminProfile = profileTypes.includes('AdminProfile');
    const hasRecipientProfile = profileTypes.includes('RecipientProfile');

    // Determine valid roles based on required profiles
    const validRoles = {
      admin: false,
      bookCreator: false,
      voiceActor: false,
      recipient: false
    };

    let isComplete = true;
    const validationIssues = [];

    for (const userType of userTypes) {
      const normalizedType = normalizeRole(userType);

      switch (normalizedType) {
        case 'admin':
          if (hasAdminProfile) {
            validRoles.admin = true;
          } else {
            isComplete = false;
            validationIssues.push('Missing AdminProfile');
          }
          break;

        case 'bookCreator':
          // BookCreator requires BOTH profiles
          if (hasBookCreatorProfile && hasVoiceActorProfile) {
            validRoles.bookCreator = true;
          } else {
            if (!hasBookCreatorProfile) {
              validationIssues.push('Missing BookCreatorProfile');
            }
            if (!hasVoiceActorProfile) {
              validationIssues.push('BookCreator missing required VoiceActorProfile');
            }
            isComplete = false;
          }
          break;

        case 'voiceActor':
          if (hasVoiceActorProfile) {
            validRoles.voiceActor = true;
          } else {
            isComplete = false;
            validationIssues.push('Missing VoiceActorProfile');
          }
          break;

        case 'recipient':
          /**
           * ⭐ A READER NEEDS ONLY ITS OWN PROFILE — unlike bookCreator, which requires BOTH its
           * own and a VoiceActorProfile. A reader is not a producer, so there is nothing else to
           * require, and demanding more would mark every reader incomplete and hide them.
           */
          if (hasRecipientProfile) {
            validRoles.recipient = true;
          } else {
            isComplete = false;
            validationIssues.push('Missing RecipientProfile');
          }
          break;

        default:
          validationIssues.push(`Unknown user type: ${userType}`);
          isComplete = false;
      }
    }

    const primaryRole = determinePrimaryRole(validRoles);

    return {
      primaryRole,
      validRoles,
      isComplete,
      hasVoiceActorProfile,
      hasBookCreatorProfile,
      hasAdminProfile,
      hasRecipientProfile,
      validationIssues
    };
  };

  // ===============================
  // ENHANCED FIELD MAPPING SYSTEM
  // ===============================

  /**
   * Profile-specific field mappings for data normalization
   */
  const PROFILE_FIELD_MAPPINGS = {
    bookCreator: {
      fields: {
        // Standard field mappings
        zipCode: 'zipcode',
        profileImageUrl: 'profilePictureUrl',
      },
      arrayFields: ['phones', 'genres', 'specialties'],
      defaults: {
        country: 'USA',
        completedProjects: 0,
      },
      imageConfig: {
        resourceType: 'bookCreatorProfileImage',
        idKey: 'creatorId'
      }
    },
    voiceActor: {
      fields: {
        // Standard field mappings
        zipCode: 'zipcode',
        profileImageUrl: 'profilePictureUrl',
        // Note: actorType is used in forms, type is used in backend
      },
      arrayFields: ['phones', 'hobbies', 'skills', 'specialties'],
      defaults: {
        country: 'USA',
        experienceLevel: 'BEGINNER',
        baseRate: 300,
        rating: 0,
        actorType: 'Authorator-ai'
      },
      imageConfig: {
        resourceType: 'voiceActorProfileImage',
        idKey: 'actorId'
      }
    },
    admin: {
      fields: {
        // Standard field mappings
        zipCode: 'zipcode',
        profileImageUrl: 'profilePictureUrl'
      },
      arrayFields: ['phones', 'permissions'],
      defaults: {
        country: 'USA',
        adminLevel: 'standard',
        department: 'general',
        canManageUsers: true,
        canManageContent: true
      },
      imageConfig: {
        resourceType: 'adminProfileImage',
        idKey: 'adminId'
      }
    }
  };

  /**
   * Normalize form data for saving to backend
   * Handles field mappings, array conversions, and defaults
   * @param {Object} values - Form values from Formik
   * @param {string} profileType - Profile type being saved
   * @returns {Object} - Normalized data ready for backend
   */
  const normalizeFormDataForSave = (values, profileType) => {
    const mapping = PROFILE_FIELD_MAPPINGS[profileType];
    if (!mapping) return values;

    let normalized = { ...values };

    // Apply field mappings (rename fields)
    Object.entries(mapping.fields).forEach(([from, to]) => {
      if (normalized[from] !== undefined) {
        normalized[to] = normalized[from];
        if (from !== to) {
          delete normalized[from];
        }
      }
    });

    // Convert comma-separated strings to arrays
    mapping.arrayFields.forEach(field => {
      if (normalized[field] && typeof normalized[field] === 'string') {
        normalized[field] = normalized[field]
          .split(',')
          .map(item => item.trim())
          .filter(Boolean);
      }
    });

    // Apply defaults
    normalized = { ...mapping.defaults, ...normalized };

    // Remove internal Formik fields and file fields
    Object.keys(normalized).forEach(key => {
      if (key.startsWith('_') ||
        key === 'profileImageFile' ||
        key === 'hasNewImageFile' ||
        key === 'profileImageUrl') {
        delete normalized[key];
      }
    });

    return normalized;
  };

  /**
   * Normalize data from backend for form display
   * Handles field mappings and array-to-string conversions
   * @param {Object} data - Data from backend
   * @param {string} profileType - Profile type being displayed
   * @returns {Object} - Normalized data ready for forms
   */
  const normalizeFormDataForDisplay = (data, profileType) => {
    const mapping = PROFILE_FIELD_MAPPINGS[profileType];
    if (!mapping) return data;

    let normalized = { ...data };

    // Reverse field mappings for display
    Object.entries(mapping.fields).forEach(([to, from]) => {
      if (normalized[from] !== undefined) {
        normalized[to] = normalized[from];
      }
    });

    // Convert arrays to comma-separated strings for form display
    mapping.arrayFields.forEach(field => {
      if (Array.isArray(normalized[field])) {
        normalized[field] = normalized[field].join(', ');
      }
    });

    // Apply defaults for missing fields
    normalized = { ...mapping.defaults, ...normalized };

    return normalized;
  };

  /**
   * Get image configuration for a profile type
   * @param {string} profileType - Profile type
   * @returns {Object} - Image upload configuration
   */
  const getImageConfig = (profileType) => {
    return PROFILE_FIELD_MAPPINGS[profileType]?.imageConfig || {
      resourceType: 'profileImage',
      idKey: 'userId'
    };
  };

  /**
   * Generate validation schema dynamically based on profile type
   * @param {string} profileType - Profile type
   * @returns {Object} - Yup validation schema
   */
  const generateValidationSchema = (profileType) => {
    const baseValidation = {
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      street: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      zipCode: Yup.string()
    };

    const profileSpecific = {
      bookCreator: {
        completedProjects: Yup.number().min(0),
        experienceYears: Yup.number().min(0),
        rating: Yup.number().min(0).max(5),
        reviewCount: Yup.number().min(0)
      },
      voiceActor: {
        actorType: Yup.string().required('Actor Type is required'),
        rating: Yup.number().min(0).max(5),
        reviewCount: Yup.number().min(0),
        baseRate: Yup.number().min(0)
      },
      admin: {
        adminLevel: Yup.string().required('Admin level is required'),
        department: Yup.string().required('Department is required'),
        canManageUsers: Yup.boolean(),
        canManageContent: Yup.boolean()
      }
    };

    return Yup.object({
      ...baseValidation,
      ...(profileSpecific[profileType] || {})
    });
  };

  /**
   * Get array fields for a profile type
   * @param {string} profileType - Profile type
   * @returns {Array<string>} - List of fields that should be arrays
   */
  const getArrayFields = (profileType) => {
    return PROFILE_FIELD_MAPPINGS[profileType]?.arrayFields || [];
  };

  /**
   * Get field mapping configuration for a profile type
   * @param {string} profileType - Profile type
   * @returns {Object} - Complete field mapping configuration
   */
  const getFieldMapping = (profileType) => {
    return PROFILE_FIELD_MAPPINGS[profileType] || null;
  };

  return {
    // Core normalization functions
    normalizeRole,
    toCypherFormat,
    fromCypherFormat,

    // Display functions
    getRoleLabel,
    getRoleIcon,
    getRoleColor,
    formatRole,

    // Configuration getters
    getRoleProfiles,
    getRoleFilterOptions,
    getRoleSelectionOptions,
    getUserTypeOptions,

    // Validation and permissions
    canHaveProfile,
    isValidRole,
    getRoleHierarchy,
    compareRoles,

    // Profile management utilities
    extractRoleFromProfileType,
    validateProfilesForRole,
    filterProfilesForRole,

    // Profile analysis and priority
    determinePrimaryRole,
    analyzeUserProfiles,

    // Profile data utilities
    getProfileDefaults,
    getEmptyProfileTemplate,
    extractBasicUserData,
    mergeProfileData,

    // Enhanced field mapping functions
    normalizeFormDataForSave,
    normalizeFormDataForDisplay,
    getImageConfig,
    generateValidationSchema,
    getArrayFields,
    getFieldMapping,

    // Export config for advanced use
    roleConfig,
    PROFILE_FIELD_MAPPINGS
  };
};

// Export the role config for use in non-component files
export const roleConfig = {
  recipient: {
    normalized: 'recipient',
    cypherFormat: 'RECIPIENT',
    label: 'Reader',
    pluralLabel: 'Readers',
    icon: 'headphones',
    color: 'teal',
    profiles: ['recipient'],
    aliases: [
      'RECIPIENT',
      'recipient',
      'receiver',
      'RECEIVER',
      'Reader',
      'reader'
    ]
  },
  admin: {
    normalized: 'admin',
    cypherFormat: 'ADMIN',
    label: 'Administrator',
    pluralLabel: 'Administrators',
    icon: 'shield',
    color: 'red',
    profiles: ['admin', 'bookCreator', 'voiceActor'],
    aliases: [
      'ADMIN',
      'admin',
      'Administrator',
      'administrator'
    ]
  },
  bookCreator: {
    normalized: 'bookCreator',
    cypherFormat: 'BOOK_CREATOR',
    label: 'Book Creator',
    pluralLabel: 'Book Creators',
    icon: 'book',
    color: 'green',
    profiles: ['bookCreator', 'voiceActor'],
    aliases: [
      'BOOK_CREATOR',
      'book_creator',
      'bookCreator',
      'Book Creator',
      'book creator'
    ]
  },
  voiceActor: {
    normalized: 'voiceActor',
    cypherFormat: 'VOICE_ACTOR',
    label: 'Voice Actor',
    pluralLabel: 'Voice Actors',
    icon: 'microphone',
    color: 'blue',
    profiles: ['voiceActor'],
    aliases: [
      'VOICE_ACTOR',
      'voice_actor',
      'voiceActor',
      'Voice Actor',
      'voice actor'
    ]
  }
};

// Export utility functions for use in non-component files
export const normalizeRoleUtil = (role) => {
  if (!role) return null;

  // Check each role config for matching alias
  for (const [key, config] of Object.entries(roleConfig)) {
    if (config.aliases.includes(role) || key === role) {
      return key;
    }
  }

  // If no match found, try lowercase comparison
  const lowercaseRole = role.toLowerCase();
  for (const [key, config] of Object.entries(roleConfig)) {
    if (config.aliases.some(alias => alias.toLowerCase() === lowercaseRole)) {
      return key;
    }
  }

  return role; // Return original if no match
};

export const toCypherFormatUtil = (role) => {
  const normalized = normalizeRoleUtil(role);
  return roleConfig[normalized]?.cypherFormat || role;
};

export const extractRoleFromProfileTypeUtil = (profileType) => {
  if (!profileType) return null;

  const profileTypeMap = {
    'AdminProfile': 'admin',
    'BookCreatorProfile': 'bookCreator',
    'VoiceActorProfile': 'voiceActor'
  };

  return profileTypeMap[profileType] || null;
};

/**
 * Utility version of determinePrimaryRole for non-component usage
 * @param {Object} validRoles - Object with boolean flags for each role
 * @returns {string|null} - Primary role based on hierarchy
 */
export const determinePrimaryRoleUtil = (validRoles) => {
  if (validRoles.admin) return 'admin';
  if (validRoles.bookCreator) return 'bookCreator';
  if (validRoles.voiceActor) return 'voiceActor';
  /**
   * ⛔ RECIPIENT IS LAST, AND THIS IS NOT THE PRIVILEGE LADDER. This answers "what do we CALL
   * this user in the UI" and nothing more. Authorization lives on the server, where the
   * recipient role deliberately returns only itself — a reader is not a weak producer, and
   * putting one on the ladder would make every "can this user do X" check wrong in the
   * generous direction. Last place means it cannot outrank a producer role for display either.
   */
  if (validRoles.recipient) return 'recipient';
  return null;
};

/**
 * Utility version of analyzeUserProfiles for non-component usage
 * @param {Array} userProfiles - Array of user profile objects
 * @returns {Object} - Analysis result
 */
export const analyzeUserProfilesUtil = (userProfiles) => {
  if (!userProfiles || !Array.isArray(userProfiles) || userProfiles.length === 0) {
    return {
      primaryRole: null,
      validRoles: { admin: false, bookCreator: false, voiceActor: false, recipient: false },
      isComplete: false,
      hasVoiceActorProfile: false,
      hasBookCreatorProfile: false,
      hasAdminProfile: false,
      hasRecipientProfile: false,
      validationIssues: ['No profiles provided']
    };
  }

  const profileTypes = userProfiles.map(p => p.profileType);
  const userTypes = [...new Set(userProfiles.map(p => p.type))];

  // Check which profiles exist
  const hasVoiceActorProfile = profileTypes.includes('VoiceActorProfile');
  const hasBookCreatorProfile = profileTypes.includes('BookCreatorProfile');
  const hasAdminProfile = profileTypes.includes('AdminProfile');
  const hasRecipientProfile = profileTypes.includes('RecipientProfile');

  // Determine valid roles based on required profiles
  const validRoles = {
    admin: false,
    bookCreator: false,
    voiceActor: false,
    recipient: false
  };

  let isComplete = true;
  const validationIssues = [];

  for (const userType of userTypes) {
    const normalizedType = normalizeRoleUtil(userType);

    switch (normalizedType) {
      case 'admin':
        if (hasAdminProfile) {
          validRoles.admin = true;
        } else {
          isComplete = false;
          validationIssues.push('Missing AdminProfile');
        }
        break;

      case 'bookCreator':
        // BookCreator requires BOTH profiles
        if (hasBookCreatorProfile && hasVoiceActorProfile) {
          validRoles.bookCreator = true;
        } else {
          if (!hasBookCreatorProfile) {
            validationIssues.push('Missing BookCreatorProfile');
          }
          if (!hasVoiceActorProfile) {
            validationIssues.push('BookCreator missing required VoiceActorProfile');
          }
          isComplete = false;
        }
        break;

      case 'voiceActor':
        if (hasVoiceActorProfile) {
          validRoles.voiceActor = true;
        } else {
          isComplete = false;
          validationIssues.push('Missing VoiceActorProfile');
        }
        break;

      case 'recipient':
        /**
         * ⭐ A READER NEEDS ONLY ITS OWN PROFILE — unlike bookCreator, which requires BOTH its
         * own and a VoiceActorProfile. A reader is not a producer, so there is nothing else to
         * require, and demanding more would mark every reader incomplete and hide them.
         */
        if (hasRecipientProfile) {
          validRoles.recipient = true;
        } else {
          isComplete = false;
          validationIssues.push('Missing RecipientProfile');
        }
        break;

      default:
        validationIssues.push(`Unknown user type: ${userType}`);
        isComplete = false;
    }
  }

  const primaryRole = determinePrimaryRoleUtil(validRoles);

  return {
    primaryRole,
    validRoles,
    isComplete,
    hasVoiceActorProfile,
    hasBookCreatorProfile,
    hasAdminProfile,
    hasRecipientProfile,
    validationIssues
  };
};

export default useRoleNormalization;
